import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable } from 'rxjs';

import {
	Integration,
	INTEGRATION_TYPE,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	RestService,
	OAuthClients,
} from 'cargo-chief-commons';

import { UrlService } from '../url/url.service';
import { Cargochief } from '../cargochief/cargochief.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '../segmentWrapper/segment-wrapper.service';
import { UtilService } from '../util/util.service';
import { ProductsService } from '@service/products/products.service';

import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { environment } from '@environment/environment';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';

@Injectable({
	providedIn: 'root',
})
export class IntegrationService {
	constructor(
		private urlService: UrlService,
		private restService: RestService,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		private utilService: UtilService,
		private dialog: MatDialog,
		private carrierVettingService: CarrierVettingService,
		public productsService: ProductsService
	) {}

	getIntegrations(integrationType: INTEGRATION_TYPE = null): Observable<Integration[]> {
		const url: string = this.urlService.listIntegrationsUrl(integrationType);
		return this.restService.GET(url);
	}

	getApiClients(): Observable<OAuthClients[]> {
		const url: string = this.urlService.getAccountAuthorizedApiClients();
		return this.restService.GET(url);
	}

	updateIntegration(url: string, integration: Integration): Observable<Integration> {
		// Avoid sending the whole account object as it is not needed
		integration['account'] = null;
		let payload: any = Object.assign({}, integration);
		return this.restService.POST(url, payload);
	}

	disconnectIntegration(url: string, integration: Integration): Observable<Integration> {
		let payload: any = Object.assign({}, integration);
		return this.restService.DELETE(url, payload);
	}

	getMyCarrierPacketsToken() {
		if (
			environment.INTEGRATIONS &&
			this.productsService.INTEGRATIONS &&
			!this.cargochief.MY_CARRIER_PACKETS_REQUEST_COMPLETED
		) {
			// const url: string = this.urlService.integrationsUrl(INTEGRATIONS.MY_CARRIER_PACKETS, null, null);
			const url: string = this.urlService.integrationsUrl();
			this.restService.GET(url).subscribe(
				(result: any) => {
					this.cargochief.setItem({ [CARGOCHIEF_KEYS.MY_CARRIER_PACKETS]: result.data });
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get MyCarrierPortal token.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
		}
	}

	showMyCarrierEmailConfirmation(
		businessName: string,
		dot: string,
		carrierId: string,
		payload: any,
		capacitySearch: any = null,
		truckLaneSearchId: string = null,
		contactName: string = null
	) {
		this.fireCarrierOnboardingEvent(SegmentEventsConstants.user_clicked_onboard_carrier, payload);
		let myCarrierPackets: any = this.cargochief.MY_CARRIER_PACKETS,
			integrationsUrl: string = '',
			emailContent: string = '',
			mcpIntegrationId!: string;

		if (IS_NOT_EMPTY(myCarrierPackets?.data)) {
			mcpIntegrationId = myCarrierPackets.id;
			myCarrierPackets = JSON.parse(myCarrierPackets.data);
		}

		if (IS_NOT_NULL_OR_UNDEFINED(myCarrierPackets.intelliviteLink)) {
			integrationsUrl += myCarrierPackets.intelliviteLink;
			if (!myCarrierPackets.intelliviteLink.endsWith(myCarrierPackets.username)) {
				integrationsUrl += IS_NOT_NULL_OR_UNDEFINED(myCarrierPackets.username)
					? `/${myCarrierPackets.username}`
					: '';
			}
			integrationsUrl += `/${dot}`;
		}

		if (IS_NOT_NULL_OR_UNDEFINED(capacitySearch)) {
			emailContent += `
				<p>
					We are working ${capacitySearch.origin.city}, ${capacitySearch.origin.state} to ${capacitySearch.destination.city}, ${capacitySearch.destination.state} and it
					looks like you would be a great match. We would like to get you setup in our network. Please click the link below to complete our MyCarrierPortal setup:
				</p>
			`;
		} else {
			emailContent += '<p>Please click the link below to complete our MyCarrierPortal setup:</p>';
		}
		contactName = IS_NOT_EMPTY(contactName) ? contactName : '&#60;carrier name&#62;';
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '700px',
			data: {
				title: `Invite ${businessName} to join your network using MyCarrierPortal.`,
				body: `
					<div class="primary-text">
						<p>Hey ${contactName},</p>
						${emailContent}
						<p>
							<a class="link" href="${integrationsUrl}" target="_blank" >
								${integrationsUrl}
							</a>
						</p>
						<br />
						<p>
							Thanks,
							<br />
							<br />
							${this.cargochief.USER_NAME}
							<br />
							${this.cargochief.USER_EMAIL} | ${this.cargochief.getUser()?.phoneNumber}
						</p>
						<br />
						<br />
					</div>
					`,
				cancelButtonText: 'Cancel',
				ConfirmButtonTest: 'Confirm',
				headerClass: 'primary-text',
				confirmationText:
					'Please click confirm and we will email the carrier and get them setup in your network.',
				confirmationTextClass: 'primary-text',
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result) && result && mcpIntegrationId) {
				this.sendMyCarrierPacketsEmail(
					mcpIntegrationId,
					dot,
					carrierId,
					payload,
					truckLaneSearchId
				);
			}
		});
	}

	sendMyCarrierPacketsEmail(
		id: string,
		dot: string,
		carrierId: string,
		payload,
		truckLaneSearchId: string
	) {
		const url = this.urlService.onboardCarriersURL(id, dot, carrierId, truckLaneSearchId);
		this.restService.GET(url).subscribe(
			(result) => {
				this.utilService.showSnackbar(result.message, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.fireCarrierOnboardingEvent(
					SegmentEventsConstants.user_confirmed_onboard_carrier,
					payload
				);
				this.carrierVettingService.carrierInvitationSent(carrierId);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to send invitation email now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	fireCarrierOnboardingEvent(eventName: string, payload: any) {
		this.segmentWrapper.fireSegmentEvent(eventName, payload);
	}

	sortIntegrations(integrations: Integration[]) {
		let enabledIntegrations = integrations.filter((integration) => integration.enabled);
		let disabledIntegrations = integrations.filter((integration) => !integration.enabled);
		enabledIntegrations = enabledIntegrations.sort((a, b) => {
			if (a.integrationType < b.integrationType) {
				return -1;
			}
			if (a.integrationType > b.integrationType) {
				return 1;
			}
			return 0;
		});
		disabledIntegrations = disabledIntegrations.sort((a, b) => {
			if (a.integrationType < b.integrationType) {
				return -1;
			}
			if (a.integrationType > b.integrationType) {
				return 1;
			}
			return 0;
		});
		return enabledIntegrations.concat(disabledIntegrations);
	}

	activeIntegrationType(integrations: Integration[]): INTEGRATION_TYPE {
		let integrationType = null;
		const activeIntegration = integrations.find((integration) => integration.enabled);
		if (IS_NOT_NULL_OR_UNDEFINED(activeIntegration)) {
			integrationType = activeIntegration.integrationType;
		}
		return integrationType;
	}

	isCarrierVettingIntegrationEnabled(): boolean {
		const activeIntegrations = this.cargochief.getActiveIntegrations();
		const rmisEnabled =
			IS_NOT_NULL_OR_UNDEFINED(activeIntegrations) &&
			activeIntegrations.includes(INTEGRATION_TYPE.RMIS);
		const myCarrierPackets: any = this.cargochief.MY_CARRIER_PACKETS;
		const mcpData =
			myCarrierPackets && myCarrierPackets.data ? JSON.parse(myCarrierPackets.data) : { data: {} };
		return mcpData.intelliviteLink || rmisEnabled;
	}
}
